import React, { createContext, useContext, useState, useCallback } from "react";
import { LOCALES } from "./locales";

const SUPPORTED = ["en", "pt", "es", "it"];
const FALLBACK = "en";

// System/UI default is English. Before authentication we detect the language
// from the browser; anything unsupported falls back to English.
function normalize(lang) {
  const base = (lang || "").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(base) ? base : null;
}

// The browser's preferred SUPPORTED language, or null when none of the browser's
// languages is one we ship (so the caller can fall back to a tenant language).
export function detectSupportedBrowserLanguage() {
  const candidates = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  for (const c of candidates) {
    const n = normalize(c);
    if (n) return n;
  }
  return null;
}

export function detectBrowserLanguage() {
  return detectSupportedBrowserLanguage() || FALLBACK;
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectBrowserLanguage);

  // Adopt a language (e.g. the authenticated user's preference); unsupported
  // values fall back to English.
  const setLanguage = useCallback((value) => {
    setLang(normalize(value) || FALLBACK);
  }, []);

  // Public screens: prefer the browser's language when it's one we ship, otherwise fall
  // back to the tenant/company language (then English). Policy for /v, OIDC consent, etc.
  const setPublicLanguage = useCallback((tenantLang) => {
    setLang(detectSupportedBrowserLanguage() || normalize(tenantLang) || FALLBACK);
  }, []);

  // Reset to the browser-detected language (e.g. on logout).
  const resetLanguage = useCallback(() => {
    setLang(detectBrowserLanguage());
  }, []);

  const t = useCallback((key, fallbackText) => {
    const table = LOCALES[lang] || LOCALES[FALLBACK];
    return table[key] ?? LOCALES[FALLBACK][key] ?? fallbackText ?? key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLanguage, setPublicLanguage, resetLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
